import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { Skeleton } from "@/components/Skeleton";
import type {
  Invite,
  TeamMember,
  UserRole,
  UserStatus,
} from "@/types";
import { roleLabel } from "@/types";

const ROLE_STYLES: Record<UserRole, string> = {
  shareholder: "border-violet-500/40 bg-violet-500/10 text-violet-300",
  executive_manager: "border-accent-500/30 bg-accent-500/10 text-accent-400",
  operations_manager: "border-accent-500/25 bg-accent-500/[0.06] text-accent-300",
  lead_broker: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  broker: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  member: "border-slate-500/30 bg-slate-500/10 text-slate-300",
};

const STATUS_STYLES: Record<UserStatus, string> = {
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  disabled: "border-rose-500/40 bg-rose-500/15 text-rose-200",
};

/** Per-role avatar ring tint — gives the team tree a quick visual hierarchy
 *  (executive/operations manager glow accent, lead broker green, broker amber)
 *  without adding another badge. Paired with `ring-offset-ink-900` in use. */
const ROLE_RING: Record<UserRole, string> = {
  shareholder: "ring-violet-400/60",
  executive_manager: "ring-accent-400/60",
  operations_manager: "ring-accent-300/50",
  lead_broker: "ring-emerald-400/50",
  broker: "ring-amber-400/50",
  member: "ring-white/15",
};

function formatDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInvite, setLastInvite] = useState<Invite | null>(null);

  async function reload() {
    try {
      const rows = await api<TeamMember[]>("/api/team");
      setMembers(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load team");
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function deactivate(memberId: string) {
    if (!confirm("Deactivate this user? Their reports will be reassigned to the Executive Manager.")) return;
    try {
      await api(`/api/team/${memberId}`, { method: "DELETE" });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to deactivate");
    }
  }

  async function reactivate(memberId: string) {
    try {
      await api(`/api/team/${memberId}`, {
        method: "PATCH",
        json: { status: "active" },
      });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reactivate");
    }
  }

  const canInvite =
    user?.role === "executive_manager" ||
    user?.role === "operations_manager" ||
    user?.role === "lead_broker";

  // Collapsed manager-group keys. Default = all expanded; clicking a group
  // header toggles it.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Build the manager → reports tree. A user with several managers appears
  // under each. Managers themselves head their own group (and still appear
  // as a report under their own managers). Users with no manager who aren't
  // a group head fall into the "No manager" bucket.
  const tree = useMemo(() => {
    const list = members ?? [];
    const byId = new Map(list.map((m) => [m.id, m]));
    const reportsByMgr = new Map<string, TeamMember[]>();
    for (const m of list) {
      for (const mgr of m.managers) {
        if (!reportsByMgr.has(mgr.id)) reportsByMgr.set(mgr.id, []);
        reportsByMgr.get(mgr.id)!.push(m);
      }
    }
    const nameOfMgr = (id: string): string => {
      const u = byId.get(id);
      if (u) return u.full_name || u.email;
      for (const m of list) {
        const f = m.managers.find((x) => x.id === id);
        if (f?.name) return f.name;
      }
      return "Manager";
    };
    const mgrIds = [...reportsByMgr.keys()].sort((a, b) =>
      nameOfMgr(a).localeCompare(nameOfMgr(b)),
    );
    const noManager = list
      .filter((m) => m.managers.length === 0 && !reportsByMgr.has(m.id))
      .sort((a, b) =>
        (a.full_name || a.email).localeCompare(b.full_name || b.email),
      );
    return { byId, reportsByMgr, mgrIds, noManager, nameOfMgr };
  }, [members]);

  const rowProps = {
    viewer: user,
    onDeactivate: deactivate,
    onReactivate: reactivate,
  };

  return (
    <div className="w-full">
      {/* Pure people-management tool: no heading, no stats — just the invite
          action (when allowed) and the roster below. */}
      {canInvite && (
        <div className="flex justify-end mb-4">
          <button
            className="btn-primary"
            onClick={() => {
              setLastInvite(null);
              setInviteOpen(true);
            }}
          >
            Invite member
          </button>
        </div>
      )}

      {error && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm mb-4">
          {error}
        </div>
      )}

      {/* Mobile card list — desktop swaps in the table at md+. The multi-column
          table is unreadable on a phone, so we render the same data as
          stacked cards instead, with avatar + role/status badges and the
          owner-only deactivate/reactivate action. */}
      <div className="md:hidden space-y-3">
        {members === null &&
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card p-3">
              <Skeleton className="h-12" />
            </div>
          ))}
        {members?.length === 0 && (
          <div className="card p-6 text-center text-slate-500 text-sm">
            Nothing yet. Use "Invite member" to add someone.
          </div>
        )}
        {members && members.length > 0 && (
          <>
            {tree.mgrIds.map((mid) => {
              const reports = tree.reportsByMgr.get(mid) ?? [];
              const isCollapsed = collapsed.has(mid);
              return (
                <div key={`g-${mid}`} className="space-y-2">
                  <GroupHeaderButton
                    label={tree.nameOfMgr(mid)}
                    count={reports.length}
                    collapsed={isCollapsed}
                    onToggle={() => toggleGroup(mid)}
                  />
                  {!isCollapsed &&
                    reports.map((r) => (
                      <MemberCard key={`${mid}-${r.id}`} m={r} ctx={rowProps} />
                    ))}
                </div>
              );
            })}
            {tree.noManager.length > 0 && (
              <div className="space-y-2">
                <GroupHeaderButton
                  label="No manager"
                  count={tree.noManager.length}
                  collapsed={collapsed.has("__none__")}
                  onToggle={() => toggleGroup("__none__")}
                />
                {!collapsed.has("__none__") &&
                  tree.noManager.map((r) => (
                    <MemberCard key={`none-${r.id}`} m={r} ctx={rowProps} />
                  ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent">
            <tr>
              <th className="text-left px-4 py-3">Member</th>
              <th className="text-left px-4 py-3">Role &amp; status</th>
              <th className="text-left px-4 py-3">Last seen</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members === null &&
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td colSpan={4} className="px-4 py-3">
                    <Skeleton className="h-5" />
                  </td>
                </tr>
              ))}
            {members?.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                  Nothing yet. Use “Invite member” to add someone.
                </td>
              </tr>
            )}
            {members &&
              members.length > 0 &&
              tree.mgrIds.map((mid) => {
                const reports = tree.reportsByMgr.get(mid) ?? [];
                const isCollapsed = collapsed.has(mid);
                const mgr = tree.byId.get(mid);
                return (
                  <ManagerGroupRows
                    key={`g-${mid}`}
                    manager={mgr}
                    managerName={tree.nameOfMgr(mid)}
                    reports={reports}
                    collapsed={isCollapsed}
                    onToggle={() => toggleGroup(mid)}
                    ctx={rowProps}
                  />
                );
              })}
            {members && members.length > 0 && tree.noManager.length > 0 && (
              <ManagerGroupRows
                key="g-none"
                manager={undefined}
                managerName="No manager"
                reports={tree.noManager}
                collapsed={collapsed.has("__none__")}
                onToggle={() => toggleGroup("__none__")}
                ctx={rowProps}
              />
            )}
          </tbody>
        </table>
      </div>

      {inviteOpen && (
        <InviteModal
          onClose={() => setInviteOpen(false)}
          onCreated={async (inv) => {
            setLastInvite(inv);
            await reload();
          }}
          managers={
            members?.filter(
              (m) =>
                m.role === "lead_broker" ||
                m.role === "operations_manager" ||
                m.role === "executive_manager",
            ) ?? []
          }
          viewerRole={user?.role ?? "member"}
        />
      )}

      {lastInvite && (
        <div className="card p-5 border-accent-500/30 bg-accent-500/[0.04]">
          <div className="text-xs uppercase tracking-wider text-accent-400 mb-2">
            Invite ready
          </div>
          <div className="text-sm text-slate-300 mb-3">
            Send this link to{" "}
            <span className="font-semibold text-slate-100">{lastInvite.email}</span>{" "}
            via Telegram, WhatsApp, or email. It expires{" "}
            {formatDate(lastInvite.expires_at)}.
          </div>
          <div className="flex gap-2">
            <input
              className="input flex-1 font-mono text-xs"
              readOnly
              value={lastInvite.accept_url}
            />
            <button
              className="btn-secondary"
              onClick={() => navigator.clipboard.writeText(lastInvite.accept_url)}
            >
              Copy
            </button>
            <button className="btn-secondary" onClick={() => setLastInvite(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Team tree (manager groups) ───────────────────────────────────────────

interface RowCtx {
  viewer: { id: string; role: string } | null;
  onDeactivate: (id: string) => void;
  onReactivate: (id: string) => void;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform ${open ? "rotate-90" : ""}`}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function GroupHeaderButton({
  label,
  count,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg bg-gradient-to-r from-white/[0.04] to-transparent border border-white/10 text-xs font-semibold text-slate-300 hover:border-white/20 hover:from-white/[0.06] transition"
    >
      <Chevron open={!collapsed} />
      <span className="truncate">{label}</span>
      <span className="text-slate-600 font-normal">· {count}</span>
    </button>
  );
}

function ManagerChips({
  managers,
}: {
  managers: { id: string; name: string | null }[];
}) {
  if (!managers.length) return <span className="text-slate-600">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {managers.map((mg) => (
        <span
          key={mg.id}
          className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/10 text-[11px] text-slate-300"
        >
          {mg.name || "—"}
        </span>
      ))}
    </div>
  );
}

function MemberActions({ m, ctx }: { m: TeamMember; ctx: RowCtx }) {
  const { viewer } = ctx;
  return (
    <>
      {viewer?.role === "executive_manager" &&
        m.id !== viewer.id &&
        m.role !== "executive_manager" &&
        (m.status === "active" ? (
          <button
            onClick={() => ctx.onDeactivate(m.id)}
            className="text-[11px] font-medium text-rose-300/80 hover:text-rose-200 px-2 py-1 rounded hover:bg-rose-500/10"
            title="Deactivate this user"
          >
            Deactivate
          </button>
        ) : (
          <button
            onClick={() => ctx.onReactivate(m.id)}
            className="text-[11px] font-medium text-emerald-300/80 hover:text-emerald-200 px-2 py-1 rounded hover:bg-emerald-500/10"
            title="Reactivate this user"
          >
            Reactivate
          </button>
        ))}
      <Link
        to={`/team/${m.id}`}
        className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-accent-300 px-2 py-1 rounded border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-accent-500/40 transition"
        title="Open employee profile"
      >
        Open →
      </Link>
    </>
  );
}

function MemberRow({
  m,
  ctx,
  indent = 0,
  group,
}: {
  m: TeamMember;
  ctx: RowCtx;
  indent?: number;
  group?: { count: number; collapsed: boolean; onToggle: () => void };
}) {
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.025] transition-colors group/row">
      <td className="px-4 py-3 relative">
        <div
          className="flex items-center gap-2 min-w-0"
          style={{ paddingLeft: indent * 22 }}
        >
          {group ? (
            <button
              type="button"
              onClick={group.onToggle}
              className="text-slate-500 hover:text-slate-200 p-0.5 -ml-1"
              aria-label="Toggle group"
            >
              <Chevron open={!group.collapsed} />
            </button>
          ) : indent > 0 ? (
            // Org-chart connector: a full-height trunk line plus a stub to the
            // avatar. Adjacent report rows have no gap between them (border-t
            // only), so each row's own full-height segment lines up with its
            // neighbours' and reads as one continuous line down the group.
            <>
              <span
                className="absolute left-[18px] inset-y-0 w-px bg-white/10"
                aria-hidden
              />
              <span
                className="absolute left-[18px] top-1/2 -translate-y-1/2 w-[12px] h-px bg-white/10"
                aria-hidden
              />
            </>
          ) : null}
          <Link
            to={`/team/${m.id}`}
            className="flex items-center gap-3 min-w-0 group/link"
          >
            <Avatar
              url={m.avatar_url}
              name={m.full_name}
              email={m.email}
              size="sm"
              status={m.presence_status}
              className={`ring-2 ring-offset-2 ring-offset-ink-900 ${ROLE_RING[m.role]}`}
            />
            <span className="min-w-0">
              <span className="block truncate text-slate-100 group-hover/link:text-accent-300 font-medium">
                {m.full_name || <span className="text-slate-500">— no name —</span>}
              </span>
              <span className="block truncate text-[11.5px] text-slate-500">
                {m.email}
              </span>
            </span>
          </Link>
          {group && (
            <span className="text-[11px] text-slate-600 whitespace-nowrap">
              · {group.count}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`badge ${ROLE_STYLES[m.role]}`}>{roleLabel(m.role)}</span>
          <span className={`badge ${STATUS_STYLES[m.status]}`}>{m.status}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-slate-500 text-xs">
        {formatDate(m.last_login_at)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <MemberActions m={m} ctx={ctx} />
        </div>
      </td>
    </tr>
  );
}

function ManagerGroupRows({
  manager,
  managerName,
  reports,
  collapsed,
  onToggle,
  ctx,
}: {
  manager: TeamMember | undefined;
  managerName: string;
  reports: TeamMember[];
  collapsed: boolean;
  onToggle: () => void;
  ctx: RowCtx;
}) {
  return (
    <>
      {manager ? (
        <MemberRow
          m={manager}
          ctx={ctx}
          group={{ count: reports.length, collapsed, onToggle }}
        />
      ) : (
        <tr className="border-t border-white/5 bg-gradient-to-r from-white/[0.025] to-transparent">
          <td colSpan={4} className="px-4 py-2.5">
            <button
              type="button"
              onClick={onToggle}
              className="flex items-center gap-2 text-xs font-semibold text-slate-300 hover:text-slate-100"
            >
              <Chevron open={!collapsed} />
              <span className="uppercase tracking-wider">{managerName}</span>
              <span className="text-slate-600 font-normal normal-case">· {reports.length}</span>
            </button>
          </td>
        </tr>
      )}
      {!collapsed &&
        reports.map((r) => (
          <MemberRow key={`${managerName}-${r.id}`} m={r} ctx={ctx} indent={1} />
        ))}
    </>
  );
}

function MemberCard({ m, ctx }: { m: TeamMember; ctx: RowCtx }) {
  return (
    <div className="card p-3 border-l-2 border-l-white/10 transition hover:border-l-accent-500/40 hover:bg-white/[0.015]">
      <div className="flex items-start gap-3">
        <Avatar
          url={m.avatar_url}
          name={m.full_name}
          email={m.email}
          size="md"
          status={m.presence_status}
          className={`ring-2 ring-offset-2 ring-offset-ink-900 ${ROLE_RING[m.role]}`}
        />
        <div className="flex-1 min-w-0">
          <Link
            to={`/team/${m.id}`}
            className="text-slate-100 hover:text-accent-300 font-medium truncate block"
          >
            {m.full_name || <span className="text-slate-500">— no name —</span>}
          </Link>
          <div className="text-xs text-slate-500 truncate">{m.email}</div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span className={`badge ${ROLE_STYLES[m.role]}`}>{roleLabel(m.role)}</span>
            <span className={`badge ${STATUS_STYLES[m.status]}`}>{m.status}</span>
          </div>
        </div>
      </div>
      <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between gap-2 text-[11px] text-slate-500">
        <div className="min-w-0">
          {m.managers.length > 0 && (
            <div className="mb-1">
              <ManagerChips managers={m.managers} />
            </div>
          )}
          <span className="text-slate-600">Last seen</span>{" "}
          <span className="text-slate-500">{formatDate(m.last_login_at)}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <MemberActions m={m} ctx={ctx} />
        </div>
      </div>
    </div>
  );
}

interface InviteModalProps {
  onClose: () => void;
  onCreated: (inv: Invite) => void | Promise<void>;
  managers: TeamMember[];
  viewerRole: UserRole;
}

function InviteModal({ onClose, onCreated, managers, viewerRole }: InviteModalProps) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  // Owner can additionally pick "operations_manager"; director picks among the
  // remaining roles (no self-multiplication). Managers don't reach the
  // role select at all (canChooseRole is false for them).
  const [role, setRole] = useState<UserRole>("member");
  const [managerId, setManagerId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canChooseRole =
    viewerRole === "shareholder" ||
    viewerRole === "executive_manager" ||
    viewerRole === "operations_manager";
  const canChooseManager =
    (viewerRole === "executive_manager" || viewerRole === "operations_manager") && role === "member";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = { email, role };
      if (fullName.trim()) body.full_name = fullName.trim();
      if (canChooseManager && managerId) body.manager_id = managerId;
      const inv = await api<Invite>("/api/team/invite", { method: "POST", json: body });
      await onCreated(inv);
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to create invite");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <form
        className="card max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div>
          <h2 className="card-title">Invite someone</h2>
          <p className="text-xs text-slate-500 mt-1">
            They'll get a one-time link to set a password and join your agency.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <div className="label">Email</div>
            <input
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          <div>
            <div className="label">Full name (optional)</div>
            <input
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Giorgi Datashvili"
            />
          </div>
          {canChooseRole && (
            <div>
              <div className="label">Role</div>
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
              >
                <option value="member">Member</option>
                <option value="broker">Broker · can see Hot Offers</option>
                <option value="lead_broker">Lead Broker</option>
                {(viewerRole === "executive_manager" || viewerRole === "shareholder") && (
                  <option value="operations_manager">Operations Manager · Executive Manager's right hand</option>
                )}
                {viewerRole === "shareholder" && (
                  <>
                    <option value="executive_manager">Executive Manager · agency principal</option>
                    <option value="shareholder">Shareholder · shareholder tier</option>
                  </>
                )}
              </select>
            </div>
          )}
          {canChooseManager && (
            <div>
              <div className="label">Reports to (optional)</div>
              <select
                className="input"
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
              >
                <option value="">— no manager —</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.full_name || m.email} ({roleLabel(m.role)})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {err && (
          <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy || !email}>
            {busy ? "Sending…" : "Create invite"}
          </button>
        </div>
      </form>
    </div>
  );
}
